/**
 * Payment schedule quotes for thank-you cadence picker (mirrors applications/payment_schedule.py).
 */
export type PaymentCadence = "monthly" | "quarterly" | "semi_annual" | "annual";

export type CadenceConfig = {
  advance_payment?: boolean;
  defer_first_months?: number;
};

const PERIODS_PER_YEAR: Record<PaymentCadence, number> = {
  monthly: 12,
  quarterly: 4,
  semi_annual: 2,
  annual: 1,
};

const PERIOD_MONTHS: Record<PaymentCadence, number> = {
  monthly: 1,
  quarterly: 3,
  semi_annual: 6,
  annual: 12,
};

export interface PaymentScheduleQuote {
  cadence: PaymentCadence;
  paymentAmount: number;
  totalPayments: number;
  upfrontPeriods: number;
  dueAtSigning: number;
  remainingPayments: number;
  financedPrincipal: number;
  deferFirstMonths: number;
  advancePayment: boolean;
}

function begPayment(pv: number, ratePerPeriod: number, nPeriods: number): number {
  if (nPeriods <= 0 || pv <= 0) return 0;
  if (ratePerPeriod === 0) return pv / nPeriods;
  const r = ratePerPeriod;
  return (pv * (r * Math.pow(1 + r, nPeriods - 1))) / (Math.pow(1 + r, nPeriods) - 1);
}

function endPayment(pv: number, ratePerPeriod: number, nPeriods: number): number {
  if (nPeriods <= 0 || pv <= 0) return 0;
  if (ratePerPeriod === 0) return pv / nPeriods;
  const r = ratePerPeriod;
  return (pv * (r * Math.pow(1 + r, nPeriods))) / (Math.pow(1 + r, nPeriods) - 1);
}

function compoundForDefer(pv: number, annualRatePct: number, deferMonths: number): number {
  if (deferMonths <= 0 || pv <= 0) return pv;
  return pv * Math.pow(1 + annualRatePct / 100, deferMonths / 12);
}

export function computePaymentScheduleQuote(params: {
  principalPrePoints: number;
  downPayment?: number;
  annualRatePct: number;
  termMonths: number;
  pointsPct?: number;
  cadence?: PaymentCadence;
  advancePayment?: boolean;
  deferFirstMonths?: number | null;
  upfrontPeriods?: number;
}): PaymentScheduleQuote {
  const {
    principalPrePoints,
    downPayment = 0,
    annualRatePct,
    termMonths,
    pointsPct = 0,
    cadence = "monthly",
    advancePayment = false,
    deferFirstMonths = null,
    upfrontPeriods,
  } = params;
  const periodsPerYear = PERIODS_PER_YEAR[cadence];
  const periodMonths = PERIOD_MONTHS[cadence];
  const nPeriods = Math.max(1, Math.round((termMonths * periodsPerYear) / 12));
  const pre = Math.max(0, principalPrePoints - downPayment);
  const pv = pre * (1 + pointsPct / 100);
  const rate = annualRatePct / 100 / periodsPerYear;

  if (cadence === "monthly") {
    const pmt = endPayment(pv, rate, nPeriods);
    const paymentAmount = Math.round(pmt * 100) / 100;
    return {
      cadence,
      paymentAmount,
      totalPayments: nPeriods,
      upfrontPeriods: 0,
      dueAtSigning: 0,
      remainingPayments: nPeriods,
      financedPrincipal: Math.round(pv * 100) / 100,
      deferFirstMonths: 0,
      advancePayment: false,
    };
  }

  if (advancePayment) {
    const upfront = upfrontPeriods ?? 1;
    const pmt = begPayment(pv, rate, nPeriods);
    const paymentAmount = Math.round(pmt * 100) / 100;
    return {
      cadence,
      paymentAmount,
      totalPayments: nPeriods,
      upfrontPeriods: upfront,
      dueAtSigning: Math.round(pmt * upfront * 100) / 100,
      remainingPayments: Math.max(0, nPeriods - upfront),
      financedPrincipal: Math.round(pv * 100) / 100,
      deferFirstMonths: 0,
      advancePayment: true,
    };
  }

  const defer = deferFirstMonths ?? periodMonths;
  const pvWorking =
    defer > 0 && defer !== periodMonths
      ? compoundForDefer(pv, annualRatePct, defer)
      : pv;
  const pmt = endPayment(pvWorking, rate, nPeriods);
  const paymentAmount = Math.round(pmt * 100) / 100;
  return {
    cadence,
    paymentAmount,
    totalPayments: nPeriods,
    upfrontPeriods: 0,
    dueAtSigning: 0,
    remainingPayments: nPeriods,
    financedPrincipal: Math.round(pv * 100) / 100,
    deferFirstMonths: defer,
    advancePayment: false,
  };
}

export const CADENCE_LABELS: Record<PaymentCadence, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annual: "Semi-annual",
  annual: "Annual",
};

export const ALL_CADENCES: PaymentCadence[] = ["monthly", "quarterly", "semi_annual", "annual"];

export function enabledCadencesFromOffer(
  offer: { enabled_cadences?: string[]; non_monthly_cadences?: Record<string, CadenceConfig> } | null | undefined
): PaymentCadence[] {
  if (offer?.enabled_cadences?.length) {
    return offer.enabled_cadences.filter((c): c is PaymentCadence =>
      ALL_CADENCES.includes(c as PaymentCadence)
    );
  }
  const keys = Object.keys(offer?.non_monthly_cadences ?? {});
  if (keys.length) {
    return keys.filter((c): c is PaymentCadence => ALL_CADENCES.includes(c as PaymentCadence));
  }
  return ["monthly"];
}

export function preferredCadenceFromOffer(
  offer: { enabled_cadences?: string[]; non_monthly_cadences?: Record<string, CadenceConfig> } | null | undefined,
  preferAnnual = false
): PaymentCadence {
  const cadences = enabledCadencesFromOffer(offer);
  if (preferAnnual && cadences.includes("annual")) return "annual";
  if (preferAnnual) {
    const firstNonMonthly = cadences.find((cadence) => cadence !== "monthly");
    if (firstNonMonthly) return firstNonMonthly;
  }
  return cadences[0] ?? "monthly";
}

export function cadenceConfigFromOffer(
  offer: { non_monthly_cadences?: Record<string, CadenceConfig> } | null | undefined,
  cadence: PaymentCadence
): CadenceConfig {
  return offer?.non_monthly_cadences?.[cadence] ?? {};
}
